import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "cn";
import { X } from "lucide-react";
import type * as React from "react";
import { IconButton } from "./button";

// v3.2 overlays on Radix Dialog (the same primitive as the web app's menus,
// selects and popovers, so a menu opened inside a sheet shares its dismiss
// layer). Phones get a bottom sheet with a grabber; wider screens a drawer
// from the right (Sheet) or a centred card (Dialog). apps/web's V3Drawer is
// this Sheet; ConfirmSheet lives in ./confirm-sheet.

const OVERLAY =
  "fixed inset-0 z-50 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0";

export const Grabber = ({
  className,
}: {
  className?: string;
}): React.JSX.Element => (
  <div
    aria-hidden
    className={cn(
      "mx-auto mt-2 h-1 w-9 shrink-0 rounded-full bg-v3-border-strong",
      className,
    )}
  />
);

interface SheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  /** Label of the close button (for screen readers and the tooltip). */
  closeLabel: string;
  /** Next to the title (e.g. "2 recordings"). */
  meta?: React.ReactNode;
  /** Icon buttons left of the close button. */
  tools?: React.ReactNode;
  /** Pinned under the content (the Ask bar, Play the recording...). */
  footer?: React.ReactNode;
  children: React.ReactNode;
  /**
   * "responsive": a bottom sheet on phones and a drawer from the right from
   * 640px (the transcript and sources drawers). "bottom": a bottom sheet on
   * every screen.
   */
  side?: "responsive" | "bottom";
  /** Drawer width from 640px. */
  width?: "default" | "wide";
  /** Clicking the scrim does not close it (e.g. while a proposal is shown). */
  disablePointerDismissal?: boolean;
  onEscapeKeyDown?: (event: KeyboardEvent) => void;
  /**
   * Focus the panel itself on open (no focus ring on the close button),
   * e.g. a form opened from a menu.
   */
  focusPanel?: boolean;
  className?: string;
  testId?: string;
}

// The v3.2 drawer (style.css .drawer): title row with the close button; the
// body scrolls, the footer stays.
export function Sheet({
  open,
  onOpenChange,
  title,
  closeLabel,
  meta,
  tools,
  footer,
  children,
  side = "responsive",
  width = "default",
  disablePointerDismissal = false,
  onEscapeKeyDown,
  focusPanel = false,
  className,
  testId,
}: SheetProps): React.JSX.Element {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className={cn(OVERLAY, "bg-black/25")} />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className={cn(
            "v3-remap fixed z-50 flex flex-col bg-v3-panel font-v3 text-v3-text shadow-v3-pop outline-hidden",
            // Class names stay literal so Tailwind finds them.
            side === "bottom"
              ? "inset-x-0 bottom-0 max-h-[88dvh] rounded-t-[20px] data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom-10 data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom-10"
              : [
                  "max-sm:inset-x-0 max-sm:bottom-0 max-sm:max-h-[88dvh] max-sm:rounded-t-[20px] max-sm:data-[state=open]:animate-in max-sm:data-[state=open]:slide-in-from-bottom-10 max-sm:data-[state=closed]:animate-out max-sm:data-[state=closed]:slide-out-to-bottom-10",
                  "sm:inset-y-2 sm:right-2 sm:max-w-[calc(100vw-16px)] sm:rounded-v3-panel sm:data-[state=open]:animate-in sm:data-[state=open]:slide-in-from-right-10 sm:data-[state=closed]:animate-out sm:data-[state=closed]:slide-out-to-right-10",
                  width === "wide" ? "sm:w-[480px]" : "sm:w-[420px]",
                ],
            className,
          )}
          data-testid={testId}
          tabIndex={focusPanel ? -1 : undefined}
          onEscapeKeyDown={onEscapeKeyDown}
          onOpenAutoFocus={
            focusPanel
              ? (event) => {
                  event.preventDefault();
                  (event.currentTarget as HTMLElement | null)?.focus();
                }
              : undefined
          }
          onPointerDownOutside={
            disablePointerDismissal
              ? (event) => event.preventDefault()
              : undefined
          }
        >
          <Grabber className={side === "bottom" ? undefined : "sm:hidden"} />
          <div className="flex h-14 shrink-0 items-center gap-2 pl-5 pr-2 sm:h-[52px]">
            {/* Meta gives way first, then the title (truncated): the tools
                and the close button always stay on screen (a long file
                name pushed Close off a phone, staging check 04/11). */}
            <DialogPrimitive.Title className="min-w-0 truncate text-[15px] font-semibold">
              {title}
            </DialogPrimitive.Title>
            {meta && (
              <span className="min-w-0 shrink-[100] truncate text-[13px] tabular-nums text-v3-text-3">
                {meta}
              </span>
            )}
            <span className="ml-auto flex shrink-0 items-center gap-0.5">
              {tools}
              <DialogPrimitive.Close asChild>
                <IconButton
                  data-testid={testId ? `${testId}-close` : undefined}
                  label={closeLabel}
                >
                  <X />
                </IconButton>
              </DialogPrimitive.Close>
            </span>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
          {footer && (
            <div className="shrink-0 px-3 pt-2 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
              {footer}
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  closeLabel: string;
  /** Buttons under the content, right-aligned from 640px. */
  actions?: React.ReactNode;
  children?: React.ReactNode;
  /** Where focus goes on open (default: the first focusable, the close button). */
  onOpenAutoFocus?: (event: Event) => void;
  className?: string;
  testId?: string;
}

// A centred card from 640px; on phones a bottom sheet (style.css .modal).
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  closeLabel,
  actions,
  children,
  onOpenAutoFocus,
  className,
  testId,
}: DialogProps): React.JSX.Element {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className={cn(OVERLAY, "bg-black/30")} />
        <DialogPrimitive.Content
          className={cn(
            "v3-remap fixed z-50 flex flex-col gap-2 bg-v3-panel font-v3 text-v3-text shadow-v3-pop outline-hidden",
            "max-sm:inset-x-0 max-sm:bottom-0 max-sm:max-h-[92dvh] max-sm:overflow-y-auto max-sm:rounded-t-[22px] max-sm:px-5 max-sm:pt-3 max-sm:pb-[max(1.5rem,env(safe-area-inset-bottom))] max-sm:data-[state=open]:animate-in max-sm:data-[state=open]:slide-in-from-bottom-8",
            "sm:left-1/2 sm:top-1/2 sm:w-[min(440px,calc(100vw-32px))] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-[18px] sm:p-6 sm:data-[state=open]:animate-in sm:data-[state=open]:zoom-in-95 sm:data-[state=open]:fade-in-0",
            className,
          )}
          data-testid={testId}
          onOpenAutoFocus={onOpenAutoFocus}
        >
          <Grabber className="mt-0 mb-1 bg-v3-surface-3 sm:hidden" />
          <div className="flex items-start gap-2">
            <DialogPrimitive.Title className="min-w-0 flex-1 text-[17px] font-semibold leading-6">
              {title}
            </DialogPrimitive.Title>
            <DialogPrimitive.Close asChild>
              <IconButton className="-mr-2 -mt-2" label={closeLabel}>
                <X />
              </IconButton>
            </DialogPrimitive.Close>
          </div>
          {description ? (
            <DialogPrimitive.Description className="text-[14px] leading-relaxed text-v3-text-2">
              {description}
            </DialogPrimitive.Description>
          ) : (
            <DialogPrimitive.Description className="sr-only">
              {title}
            </DialogPrimitive.Description>
          )}
          {children}
          {actions && (
            <div className="mt-3 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              {actions}
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

// Kept here so `@rosc/ui/v32/sheet` still exports every v3.2 overlay.
export { ConfirmSheet } from "./confirm-sheet";
